import React, { createContext, useState, useEffect, useContext, ReactNode, useRef } from 'react'
import Pusher, { Channel } from 'pusher-js'

// Define the types for the context
interface PusherContextType {
  pusher: Pusher | null
  channels: { [key: string]: Channel }
  setChannels: React.Dispatch<React.SetStateAction<{ [key: string]: Channel }>>
}

const PusherContext = createContext<PusherContextType | undefined>(undefined)

// Define the types for the provider's props
interface PusherProviderProps {
  children: ReactNode
  pusherKey: string
}

// Create a provider component
export const PusherProvider: React.FC<PusherProviderProps> = ({ children, pusherKey }) => {
  const [pusher, setPusher] = useState<Pusher | null>(null)
  const [channels, setChannels] = useState<{ [key: string]: Channel }>({})

  useEffect(() => {
    const pusherInstance = new Pusher(pusherKey, {
      wsHost: 'sockets.rmt1.cc',
      wsPort: 6001,
      wssPort: 6002,
      forceTLS: false,
      disableStats: true,
      enabledTransports: ['ws', 'wss'],
    })

    setPusher(pusherInstance)

    return () => {
      // Unsubscribe from all channels on cleanup
      Object.values(channels).forEach((channel) => channel.unsubscribe())
      pusherInstance.disconnect()
    }
  }, [])

  return <PusherContext.Provider value={{ pusher, channels, setChannels }}>{children}</PusherContext.Provider>
}

/**
 * usePusher is a custom hook that allows you to subscribe to a Pusher channel and listen for events.
 *
 * @param channelName - The name of the Pusher channel to subscribe to.
 * @param eventName - The name of the event to listen for.
 * @param callback - A callback function that will be called when the event is received.
 * @returns The Pusher channel instance.
 */
export const usePusher = <T,>(channelName: string, eventName: string, callback?: (data: T) => void): Channel | null => {
  const context = useContext(PusherContext)

  if (!context) {
    throw new Error('usePusher must be used within a PusherProvider')
  }

  const { pusher, channels } = context
  const [channel, setChannel] = useState<Channel | null>(null)

  useEffect(() => {
    if (pusher && channelName) {
      // Check if the channel is already subscribed
      let channelInstance = channels[channelName]

      if (!channelInstance) {
        // If not already subscribed, create and subscribe
        channelInstance = pusher.subscribe(channelName)
        context.setChannels((prevChannels) => ({
          ...prevChannels,
          [channelName]: channelInstance,
        }))
      }

      setChannel(channelInstance)

      if (callback) {
        // Bind the event to the callback function
        channelInstance.bind(eventName, callback)
      }

      return () => {
        if (channelInstance) {
          // Unbind the event and unsubscribe from the channel
          channelInstance.unbind(eventName, callback)
        }
      }
    }
  }, [pusher, channelName, eventName, callback, channels])

  return channel
}
